import { useQuery } from '@tanstack/react-query';
import { type JSX, useMemo } from 'react';
import { FiAnchor, FiRepeat } from 'react-icons/fi';

import { imageVariantLink } from '@/common/media';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import {
  type Contract,
  contractEndingSoon,
  mapContract,
} from '@/store/contracts/contracts.mappers';
import {
  getCrewList,
  getPastCrew,
  getPeopleByIds,
  getVesselInvites,
} from '@/store/persons/person.api';
import type {
  CrewMemberResponse,
  PastCrewMember,
  PersonDocument,
  PersonPresenceStatus,
  VesselInviteResponse,
} from '@/store/persons/person.dto';
import { isOwnerGroupRole } from '@/utils/roles';

/**
 * The crew roster, read once.
 *
 * Two screens ask the same questions of the same records — the roster and one
 * member's page — and the Manage header's breadcrumb needs the member's name to
 * label its leaf. Three callers deriving their own rows is how they drift apart,
 * so the reads, the row shape and everything computed from it live here. All
 * three share query keys, which is also what makes clicking a card cost nothing:
 * the page it opens is reading the cache the roster just filled.
 */

export const CREW_PATH = '/manage/crew';

// The roster card's full-bleed 120×148 photo — larger than any avatar it also feeds.
const CREW_PHOTO_WIDTH = 120;
const CREW_PHOTO_HEIGHT = 148;

export const crewMemberPath = (id: string): string => `${CREW_PATH}/${encodeURIComponent(id)}`;

/* ─────────────────────────────── One member ─────────────────────────────── */

/**
 * A roster entry, whichever of the three lists it came from. `isInvite` and
 * `isPast` say which; the raw response is kept alongside because contacts and
 * emergency contacts only exist on the crew and past-crew shapes.
 */
export type DisplayRow = {
  id: string;
  firstName: string;
  lastName?: string;
  avatar?: string;
  role: string;
  joined?: string;
  documents: PersonDocument[];
  note?: string;
  presence?: PersonPresenceStatus;
  leaveStart?: string | null;
  leaveEnd?: string | null;
  isInvite?: boolean;
  isPast?: boolean;
  crewData?: CrewMemberResponse;
  inviteData?: VesselInviteResponse;
  /** Every pending row for this invitee: cancelling only `id` would leave them invited. */
  inviteIds?: string[];
  pastData?: PastCrewMember;
  /** A temporary member's contract, or the one a pending temporary hire offers. */
  contract?: Contract;
};

/**
 * Where a member stands aboard. `off_board` is written by the dashboard widget
 * and must be rendered as a chip rather than hidden, even though it is not
 * offered as a selection target in the chip's menu.
 */
export type BoardStatus = 'on_board' | 'on_leave' | 'off_board';

export const BOARD_STATUS_LABEL: Record<BoardStatus, string> = {
  on_board: 'Aboard',
  on_leave: 'On Leave',
  off_board: 'Ashore',
};

export const BOARD_STATUS_OPTIONS: { value: BoardStatus; label: string; icon: JSX.Element }[] = [
  { value: 'on_board', label: 'Aboard', icon: <FiAnchor /> },
  { value: 'on_leave', label: 'On Leave', icon: <FiRepeat /> },
];

export const isBoardStatus = (value: string | undefined): value is BoardStatus =>
  value === 'on_board' || value === 'on_leave' || value === 'off_board';

export const getMemberPresence = (member: DisplayRow): BoardStatus | null => {
  if (member.isInvite || member.isPast) return null;
  return isBoardStatus(member.presence) ? member.presence : null;
};

/** The four things a crew status can be — presence, plus the two derived ones. */
export type CrewStatusKey = BoardStatus | 'invited' | 'accepted' | 'past';

/**
 * **Where a crew member stands**, in one word.
 *
 * The roster's card and the member's own page both state this, and answering it
 * twice is how they come to disagree — so the rule lives here and each screen
 * only decides how to draw it. A key rather than a class, so the derivation owes
 * nothing to any one stylesheet.
 *
 * Null when there is nothing to state: an active member whose presence was never
 * set. A chip reading "Crew" would be a fact the record does not hold.
 * `off_board` (written by the widget) is rendered as a chip; the menu still
 * offers only `on_board` / `on_leave` as selection targets.
 */
export const crewStatus = (
  member: DisplayRow,
): { key: CrewStatusKey; label: string; settable: boolean } | null => {
  // A temporary hire who accepted before the contract starts is still an invitation row.
  if (member.isInvite && member.inviteData?.accepted_at) {
    return { key: 'accepted', label: 'Accepted', settable: false };
  }
  if (member.isInvite) return { key: 'invited', label: 'Invited', settable: false };
  if (member.isPast) return { key: 'past', label: 'Past Crew', settable: false };
  if (!isBoardStatus(member.presence)) return null;
  return { key: member.presence, label: BOARD_STATUS_LABEL[member.presence], settable: true };
};

export const getMemberRole = (member: DisplayRow): string => member.role ?? '-';

export const getMemberName = (member: DisplayRow): string =>
  `${member.firstName} ${member.lastName ?? ''}`.trim();

/** For an invitation, the day it was sent. */
export const getMemberJoinDate = (member: DisplayRow): string => {
  const date = member.isInvite ? member.inviteData?.created_at : member.joined;
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

// Absence dates are calendar days: the API stores a Postgres `date` and returns it as
// `YYYY-MM-DDT00:00:00Z`. Never let a viewer's time zone shift the day — read the first ten
// characters, and send UTC midnight of the day the user picked.
const absenceDayParts = (wire: string): [number, number, number] => {
  const [y, m, d] = wire.slice(0, 10).split('-').map(Number);
  return [y, m, d];
};

/** Wire value for the API from the date picker's ISO instant: UTC midnight of the picked local day. */
export const toAbsenceWireDate = (pickerIso: string): string => {
  const picked = new Date(pickerIso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${picked.getFullYear()}-${pad(picked.getMonth() + 1)}-${pad(picked.getDate())}T00:00:00Z`;
};

/** Picker value for a stored absence date: local midnight of the stored calendar day. */
export const fromAbsenceWireDate = (wire: string): string => {
  const [y, m, d] = absenceDayParts(wire);
  return new Date(y, m - 1, d).toISOString();
};

const fmtAbsenceDate = (wire: string): string => {
  const [y, m, d] = absenceDayParts(wire);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** "Away: Sep 15 – Sep 20", "Away since Sep 15", or null when no dates are set. */
export const formatAbsenceLine = (
  leaveStart?: string | null,
  leaveEnd?: string | null,
): string | null => {
  if (leaveStart && leaveEnd)
    return `Away: ${fmtAbsenceDate(leaveStart)} – ${fmtAbsenceDate(leaveEnd)}`;
  if (leaveStart) return `Away since ${fmtAbsenceDate(leaveStart)}`;
  return null;
};

/** Email and phone, from whichever of the two shapes carries contacts. */
export const getMemberContacts = (member: DisplayRow): { email?: string; phone?: string } => {
  const contacts = member.crewData?.contacts ?? member.pastData?.contacts ?? [];
  const email = contacts.find((c) => c.contact_type === 'email')?.contact;
  const phone = contacts.find((c) => c.contact_type === 'phone')?.contact;
  // An invitation has no contact list — it was *sent* to one address, which is
  // the only thing known about how to reach them.
  if (member.isInvite) {
    const inviteContact = member.inviteData?.contact;
    const inviteIsEmail = inviteContact?.includes('@') ?? false;
    return {
      email: email ?? (inviteIsEmail ? inviteContact : undefined),
      phone: phone ?? (inviteContact && !inviteIsEmail ? inviteContact : undefined),
    };
  }
  return { email, phone };
};

/* ─────────────────────────────── Certificates ────────────────────────────── */

export const EXPIRY_WARNING_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole days from now until `value`; negative once the date has passed. */
export const daysUntil = (value: string): number =>
  Math.ceil((new Date(value).getTime() - Date.now()) / MS_PER_DAY);

export const dayCount = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'}`;

/**
 * The warning a contract close to its end wears, or null while it is not. Days left count
 * today, so a contract on its last day "ends today".
 */
export const contractEndingPill = (contract: Contract | undefined): string | null => {
  if (!contract || !contractEndingSoon(contract)) return null;
  if (contract.daysLeft <= 0) return 'Ended';
  if (contract.daysLeft === 1) return 'Ends today';
  return `Ends in ${dayCount(contract.daysLeft)}`;
};

export type CertTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export type CertExpiry = {
  date: string;
  /** Pill text. Short, but never only a colour (1.4.1). */
  label: string;
  tone: CertTone;
  /** The same fact said in full — the sentence the pill abbreviates. */
  detail: string;
};

/**
 * Where one document's expiry stands, or null when it carries no date.
 *
 * The pill states it in a word and the detail line says it in full, because a
 * date on its own does not tell you whether it has passed and "Expiring" on its
 * own does not tell you when. A valid certificate's day count is left out: two
 * years out, the date is the only part anyone reads.
 */
export const certExpiry = (value: string | null | undefined): CertExpiry | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const formatted = date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const days = daysUntil(value);

  if (days < 0) {
    return {
      date: formatted,
      label: 'Expired',
      tone: 'danger',
      detail: `Expired ${formatted} · ${dayCount(-days)} ago`,
    };
  }
  if (days === 0) {
    return {
      date: formatted,
      label: 'Expires Today',
      tone: 'warning',
      detail: `Expires today, ${formatted}`,
    };
  }
  if (days <= EXPIRY_WARNING_DAYS) {
    return {
      date: formatted,
      label: 'Expiring',
      tone: 'warning',
      detail: `Expires ${formatted} · in ${dayCount(days)}`,
    };
  }
  return { date: formatted, label: 'Valid', tone: 'success', detail: `Expires ${formatted}` };
};

/**
 * **What separates a certificate from a file:** the expiry date, and nothing
 * else.
 *
 * Both are the same record on the same endpoint — one list on the member, one
 * upload path, one delete path — so the only thing that can tell them apart is
 * whether the record says when it lapses. A dated document is a certificate: it
 * feeds the roster pill, the "needs renewing" count, and everything built on
 * them. An undated one is a file: paperwork worth keeping on the record that
 * nobody has to chase.
 *
 * The rule lives here because both halves of the member's page read it and the
 * roster card counts by it — deciding it twice is how a document ends up in
 * both sections, or in neither.
 */
export const isCertificate = (doc: PersonDocument): boolean => !!doc.expiration_date;

/** True for the documents that need chasing — expired, or inside the window. */
export const needsRenewal = (doc: PersonDocument): boolean => {
  const expiry = certExpiry(doc.expiration_date);
  return !!expiry && expiry.tone !== 'success';
};

/** The three states a dated certificate can be in, along the timeline. */
export type CertBucketKey = 'valid' | 'expiring' | 'expired';

export type CertBucket = {
  key: CertBucketKey;
  /** Badge text — the count and what it counts, e.g. "2 expiring". */
  label: string;
  tone: CertTone;
  count: number;
};

/**
 * Always in this order, so a badge sits in the same place on every card and the
 * eye learns where to find the red one. Sound first, because that is the order
 * the dates themselves run in.
 */
const CERT_BUCKETS: { key: CertBucketKey; word: string; tone: CertTone }[] = [
  { key: 'valid', word: 'valid', tone: 'success' },
  { key: 'expiring', word: 'expiring', tone: 'warning' },
  { key: 'expired', word: 'expired', tone: 'danger' },
];

/** Which bucket a day count falls in. Shares `certExpiry`'s warning window. */
const certBucket = (days: number): CertBucketKey =>
  days < 0 ? 'expired' : days <= EXPIRY_WARNING_DAYS ? 'expiring' : 'valid';

/**
 * **The papers, in three counts:** how many are sound, how many are running
 * out, how many have lapsed.
 *
 * One pill carrying only the worst of them answered a single question and hid
 * the rest: "5 · Expired" could not tell one lapsed certificate among five from
 * five lapsed ones, and it never mentioned the sound papers at all. Three
 * badges state the whole standing at a glance, and they are the same three
 * buckets the certificate list on the member's own page sorts into.
 *
 * A bucket nobody is in is left out rather than drawn as a zero — "0 expired"
 * in red is a warning about nothing, and on a roster where most people are in
 * good standing it would be two badges in three saying so.
 *
 * Undated documents are left out entirely: they are the member's files rather
 * than their certificates (see `isCertificate`), and counting them here made
 * the pills promise papers that expire about paperwork that never does.
 *
 * Reads the same window as `certExpiry`, so the roster card and the certificate
 * list on the member's own page cannot disagree about when a date needs chasing.
 */
export const certSummary = (
  documents: PersonDocument[] | undefined,
): { buckets: CertBucket[]; spoken: string } | null => {
  const dated = (documents ?? [])
    .filter(isCertificate)
    .map((doc) => doc.expiration_date)
    .filter((value): value is string => !!value && !Number.isNaN(new Date(value).getTime()));
  if (!dated.length) return null;

  const counts = dated.reduce<Record<CertBucketKey, number>>(
    (acc, value) => {
      acc[certBucket(daysUntil(value))] += 1;
      return acc;
    },
    { valid: 0, expiring: 0, expired: 0 },
  );

  const buckets = CERT_BUCKETS.filter(({ key }) => counts[key] > 0).map(({ key, word, tone }) => ({
    key,
    tone,
    count: counts[key],
    label: `${counts[key]} ${word}`,
  }));

  return {
    buckets,
    // The badges said in words, for the card's label: "2 expired" on its own
    // does not say what expired.
    spoken: buckets
      .map(({ count, key }) => `${count} ${key} ${count === 1 ? 'certificate' : 'certificates'}`)
      .join(', '),
  };
};

/** Worst first, so the list leads with what needs chasing. Undated last. */
export const byCertUrgency = (a: PersonDocument, b: PersonDocument): number => {
  const left = a.expiration_date ? daysUntil(a.expiration_date) : Number.POSITIVE_INFINITY;
  const right = b.expiration_date ? daysUntil(b.expiration_date) : Number.POSITIVE_INFINITY;
  return left - right;
};

/* ──────────────────────────────── The reads ──────────────────────────────── */

export const CREW_LIST_KEY = 'crew-list';
export const CREW_INVITES_KEY = 'vessel-invites';
export const PAST_CREW_KEY = 'past-crew';
export const CREW_PEOPLE_KEY = 'crew-people';

/**
 * **The vessel the roster reads, and the id its every query is keyed by.**
 *
 * The active account, not `useVessels().vessel`. These endpoints are scoped by
 * the `X-Service-Id` header, so the id is the whole of what they need — and the
 * active account is settled before the first route paints
 * (`resolveActiveAccount`, which Layout awaits), where the vessel *record* only
 * appears once `getVesselsByIds` has come back.
 *
 * Waiting for the record was what made both screens paint an empty state, then a
 * loader, then the roster: with no id every read was `enabled: false`, a disabled
 * query reports `isLoading: false`, and "nothing loading, no rows" is exactly how
 * an empty roster looks. Reading the account instead, the requests go out on the
 * first render and the id is missing only when the active account genuinely is
 * not a vessel.
 */
export const useRosterVesselId = (): string | undefined => {
  const serviceId = useActiveAccount((state) => state.serviceId);
  const serviceType = useActiveAccount((state) => state.serviceType);
  return serviceType === 'vessel' ? serviceId : undefined;
};

export const useCrewRoster = () => {
  const vesselId = useRosterVesselId();

  const {
    data: crewListData = [],
    isLoading: isCrewListLoading,
    refetch: refetchCrew,
  } = useQuery({
    queryKey: [CREW_LIST_KEY, vesselId],
    queryFn: () => getCrewList(),
    enabled: !!vesselId,
  });

  const { data: vesselInvitesData = [], isLoading: isInvitesLoading } = useQuery({
    queryKey: [CREW_INVITES_KEY, vesselId],
    queryFn: () => getVesselInvites(),
    enabled: !!vesselId,
  });

  const {
    data: pastCrewData = [],
    isLoading: isPastCrewLoading,
    refetch: refetchPastCrew,
  } = useQuery({
    queryKey: [PAST_CREW_KEY, vesselId],
    queryFn: () => getPastCrew(),
    enabled: !!vesselId,
  });

  /** Everyone the three lists above named, once, in a stable order. */
  const rosterPersonIds = useMemo(() => {
    const ids = [
      ...crewListData.map((c) => c.person_id),
      ...vesselInvitesData.map((i) => i.person_id),
      ...pastCrewData.map((p) => p.person_id),
    ].filter(Boolean);
    return [...new Set(ids)].sort();
  }, [crewListData, vesselInvitesData, pastCrewData]);

  /**
   * Avatars and presence.
   *
   * A second read is unavoidable: `CrewMemberResponse` carries neither, and
   * presence lives on `PersonResponse.services[].presence` — but this one is a
   * filter rather than a scan. `GET /ids` takes exactly the person ids the three
   * lists already named, so the request grows with the roster and nothing else.
   */
  const { data: people = [], isLoading: isPeopleLoading } = useQuery({
    queryKey: [CREW_PEOPLE_KEY, vesselId, rosterPersonIds],
    queryFn: () => getPeopleByIds(rosterPersonIds),
    enabled: rosterPersonIds.length > 0,
    // Nothing here is worth holding on to: presence is written from the roster
    // and from the dashboard's who's-on-board widget, and the key changes with
    // the roster anyway.
    gcTime: 0,
  });

  const crewRows: DisplayRow[] = useMemo(() => {
    const peopleMap = new Map(people.map((p) => [p.id, p]));
    const seen = new Set<string>();
    return crewListData
      .filter((c) => c.role !== 'guest' && !isOwnerGroupRole(c.role))
      .filter((c) => {
        if (seen.has(c.person_id)) return false;
        seen.add(c.person_id);
        return true;
      })
      .map((c) => {
        const person = peopleMap.get(c.person_id);
        const serviceRole = person?.services?.find((s) => s.service_id === c.service_id);
        return {
          id: c.person_id,
          firstName: c.first_name,
          lastName: c.last_name,
          avatar:
            imageVariantLink(person?.avatar, CREW_PHOTO_WIDTH, CREW_PHOTO_HEIGHT) ?? undefined,
          role: c.role,
          joined: c.joined,
          documents: c.documents,
          note: c.note,
          presence: (serviceRole?.presence as PersonPresenceStatus) ?? c.presence ?? undefined,
          leaveStart: c.leave_start ?? null,
          leaveEnd: c.leave_end ?? null,
          crewData: c,
          contract: mapContract(c.contract),
        };
      });
  }, [crewListData, people]);

  const inviteRows: DisplayRow[] = useMemo(() => {
    const peopleMap = new Map(people.map((p) => [p.id, p]));
    const activeIds = new Set(crewRows.map((row) => row.id));
    // Nothing stops the same person being invited twice, so one card per invitee holds all
    // of their rows — newest first, as the list comes.
    const byInvitee = new Map<string, DisplayRow>();
    for (const inv of vesselInvitesData) {
      // An invitation code names nobody; its list lives on the vessel page, beside the crew.
      if (
        inv.type === 'code' ||
        inv.role === 'guest' ||
        isOwnerGroupRole(inv.role) ||
        activeIds.has(inv.person_id)
      ) {
        continue;
      }
      const key = inv.person_id || inv.contact || inv.id;
      const seen = byInvitee.get(key);
      if (seen) {
        seen.inviteIds?.push(inv.id);
        continue;
      }
      byInvitee.set(key, {
        id: inv.id,
        firstName: inv.name,
        avatar:
          imageVariantLink(
            peopleMap.get(inv.person_id)?.avatar,
            CREW_PHOTO_WIDTH,
            CREW_PHOTO_HEIGHT,
          ) ?? undefined,
        role: inv.role,
        documents: inv.documents,
        note: inv.note,
        isInvite: true,
        inviteData: inv,
        inviteIds: [inv.id],
        contract: mapContract(inv.contract),
      });
    }
    return [...byInvitee.values()];
  }, [vesselInvitesData, crewRows, people]);

  const pastRows: DisplayRow[] = useMemo(() => {
    const peopleMap = new Map(people.map((p) => [p.id, p]));
    const activeIds = new Set(crewRows.map((row) => row.id));
    const seen = new Set<string>();
    return pastCrewData
      .filter((p) => p.role !== 'guest' && !isOwnerGroupRole(p.role))
      .filter((p) => !activeIds.has(p.person_id))
      .filter((p) => {
        const key = p.person_id || p.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((p) => ({
        id: p.id,
        firstName: p.first_name,
        lastName: p.last_name,
        avatar:
          imageVariantLink(
            peopleMap.get(p.person_id)?.avatar,
            CREW_PHOTO_WIDTH,
            CREW_PHOTO_HEIGHT,
          ) ?? undefined,
        role: p.role,
        joined: p.joined,
        documents: p.documents,
        note: p.note,
        isPast: true,
        pastData: p,
      }));
  }, [pastCrewData, crewRows, people]);

  return {
    crewRows,
    inviteRows,
    pastRows,
    vesselId: vesselId ?? null,
    isCrewListLoading,
    isInvitesLoading,
    isPastCrewLoading,
    // The people read can only start once the lists have named who to ask for,
    // so it lands second. A disabled query reports `isLoading: false`, so an
    // empty roster does not hang on it.
    isPeopleLoading,
    refetchCrew,
    refetchPastCrew,
  };
};

/**
 * One member by id, from whichever list holds them.
 *
 * `loading` is every read the lookup depends on, because "not found" and "not
 * loaded yet" render very differently and getting them the wrong way round
 * shows a not-found page for a record that is on its way.
 */
export const useCrewMember = (id: string | undefined) => {
  const roster = useCrewRoster();
  const { crewRows, inviteRows, pastRows } = roster;

  const member = useMemo(
    // An invitee's older invitation ids lead to their one card too.
    () =>
      [...crewRows, ...inviteRows, ...pastRows].find(
        (row) => row.id === id || (!!id && row.inviteIds?.includes(id)),
      ),
    [crewRows, inviteRows, pastRows, id],
  );

  return {
    ...roster,
    member,
    loading:
      roster.isCrewListLoading ||
      roster.isInvitesLoading ||
      roster.isPastCrewLoading ||
      roster.isPeopleLoading,
  };
};
